/** Live preview + test-send for a notification template being edited.
 *
 * Shared by the org (NotificationTemplatesCard) and campaign
 * (CampaignNotificationTemplatesCard) editors. Debounces the current
 * subject/body, asks the backend to render it exactly as a recipient
 * would receive it (branded email HTML, or interpolated SMS + char
 * count), and offers a "send test" button — email always, SMS only when
 * the org has SMS enabled. See apps/sessions/template_preview.py. */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import PhoneInput from "@/components/PhoneInput";
import { ApiPaths, api } from "@/lib/api";
import type { Organization } from "@/lib/types";

type PreviewResp = {
  channel: "email" | "sms";
  subject: string;
  body_text: string;
  html: string | null;
  sms_length?: number;
  sms_over_limit?: boolean;
};

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

export function TemplatePreviewAndTest({
  kind,
  channel,
  subject,
  bodyText,
  campaignId,
}: {
  kind: string;
  channel: string;
  subject: string;
  bodyText: string;
  /** When set, the preview resolves this campaign's {cf_*} custom-field
   *  sample values (campaign editor); omit for the org editor. */
  campaignId?: string;
}) {
  const { t } = useTranslation();
  const dSubject = useDebounced(subject, 400);
  const dBody = useDebounced(bodyText, 400);

  const org = useQuery({
    queryKey: ["org-me"],
    queryFn: async () => (await api.get<Organization>(ApiPaths.orgMe)).data,
  });
  const smsEnabled = !!org.data?.sms_enabled;

  const preview = useQuery({
    queryKey: ["nt-preview", kind, channel, dSubject, dBody, campaignId],
    queryFn: async () =>
      (
        await api.post<PreviewResp>(ApiPaths.notificationTemplatePreview, {
          kind,
          channel,
          subject: dSubject,
          body_text: dBody,
          ...(campaignId ? { campaign: campaignId } : {}),
        })
      ).data,
    placeholderData: (prev) => prev,
  });

  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const sendTest = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ ok: boolean; reason?: string }>(
          ApiPaths.notificationTemplateSendTest,
          {
            kind,
            channel,
            subject,
            body_text: bodyText,
            recipient_email: channel === "email" ? testEmail.trim() : "",
            recipient_phone: channel === "sms" ? testPhone.trim() : "",
          },
        )
      ).data,
  });

  const data = preview.data;

  return (
    <div className="space-y-3 rounded-md border border-nb-border bg-slate-900/30 p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
        {t("admin.notifications.previewTitle", "Preview")}
      </h3>

      {preview.isLoading && !data && (
        <p className="text-xs text-nb-text-3">
          {t("admin.notifications.previewLoading", "Rendering…")}
        </p>
      )}

      {data && channel === "email" && data.html && (
        <>
          <p className="text-xs text-nb-text-3">
            {t("admin.notifications.previewSubjectLabel", "Subject")}:{" "}
            <span className="text-nb-text">{data.subject || "—"}</span>
          </p>
          {/* Fully sandboxed — the rendered HTML embeds the (admin-editable)
              body, so block scripts + same-origin access outright. */}
          <iframe
            title={t("admin.notifications.previewTitle", "Preview")}
            srcDoc={data.html}
            sandbox=""
            className="h-96 w-full rounded border border-nb-border bg-white"
          />
        </>
      )}

      {data && channel === "sms" && (
        <div className="space-y-1">
          <pre className="whitespace-pre-wrap rounded border border-nb-border bg-nb-surface/40 p-2 text-xs text-nb-text">
            {data.body_text || "—"}
          </pre>
          <p
            className={
              data.sms_over_limit ? "text-xs text-cvp-danger" : "text-xs text-nb-text-3"
            }
          >
            {t("admin.notifications.smsChars", "{{count}} characters", {
              count: data.sms_length ?? 0,
            })}
          </p>
        </div>
      )}

      {/* ---- Send test ---- */}
      <div className="space-y-2 border-t border-nb-border pt-3">
        <h4 className="text-xs font-semibold text-nb-text-2">
          {t("admin.notifications.sendTestTitle", "Send a test")}
        </h4>
        {channel === "email" ? (
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs">
              <span className="block text-nb-text-3">
                {t("admin.notifications.testEmailLabel", "Test email address")}
              </span>
              <input
                type="email"
                className="input"
                value={testEmail}
                placeholder="you@example.com"
                onChange={(e) => setTestEmail(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={!testEmail.trim() || sendTest.isPending}
              onClick={() => sendTest.mutate()}
            >
              {sendTest.isPending
                ? t("admin.notifications.sending", "Sending…")
                : t("admin.notifications.sendTestEmail", "Send test email")}
            </button>
          </div>
        ) : smsEnabled ? (
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs">
              <span className="block text-nb-text-3">
                {t("admin.notifications.testPhoneLabel", "Test phone number")}
              </span>
              <PhoneInput
                value={testPhone}
                placeholder="+33612345678"
                onChange={setTestPhone}
              />
            </label>
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={!testPhone.trim() || sendTest.isPending}
              onClick={() => sendTest.mutate()}
            >
              {sendTest.isPending
                ? t("admin.notifications.sending", "Sending…")
                : t("admin.notifications.sendTestSms", "Send test SMS")}
            </button>
          </div>
        ) : (
          <p className="text-xs text-nb-text-3">
            {t(
              "admin.notifications.smsDisabledNote",
              "SMS is not enabled for this organization.",
            )}
          </p>
        )}
        {sendTest.data?.ok && (
          <p className="text-xs text-nb-mint">
            {t("admin.notifications.testSent", "Test sent.")}
          </p>
        )}
        {sendTest.data && !sendTest.data.ok && (
          <p className="text-xs text-cvp-danger">
            {sendTest.data.reason ||
              t("admin.notifications.testFailed", "Send failed.")}
          </p>
        )}
        {sendTest.isError && (
          <p className="text-xs text-cvp-danger">
            {t("admin.notifications.testFailed", "Send failed.")}
          </p>
        )}
      </div>
    </div>
  );
}
